import React from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useHistory} from 'react-router-dom';
import {
  Col,
  UiBox,
  UiButton,
  UiFormControl,
  UiFormErrorMessage,
  UiQuestionnaireLayout,
  UiSimpleGrid,
  UiText,
  UiTextarea,
} from '~/components/elements';
import {PAGE_URLS} from '~/constants';
import {WEIGHT_LOSS_TWO_KEYS, WEIGHT_LOSS_TWO_QUESTIONNAIRES, YES_NO_VALUES} from '~/constants/medical-questionnaires';
import {useInput} from '~/hooks';
import {setQuestionnaireAnswer} from '~/redux/questionnaires/slice';
import {submitOrLogMdq} from '~/redux/questionnaires/thunk';
import {RootState} from '~/redux/root-reducer';
import {AppDispatch} from '~/redux/root-store';

interface Props {}

const MDQ2Questionnaire7Screen = ({}: Props) => {
  const dispatch: AppDispatch = useDispatch();
  const history = useHistory();
  const mdq = useSelector((state: RootState) => state.questionnaires);
  const {methodKey, medicalUUID, answerOrder} = mdq;
  //   const medicalQuestionnaire = useSelector((state: RootState) => state.questionnaires.medicalQuestionnaire);
  //   const {wpAuthUser} = medicalQuestionnaire;

  //   const questionnaires = medicalQuestionnaire?.questionnaires;
  const questionnaire = WEIGHT_LOSS_TWO_QUESTIONNAIRES.find(
    i => i.key === WEIGHT_LOSS_TWO_KEYS.TAKE_WEIGHT_LOSS_MEDICATION_BEFORE,
  );

  const currentAnswer = useSelector(
    (state: RootState) =>
      state.questionnaires?.collectedData[WEIGHT_LOSS_TWO_KEYS.TAKE_WEIGHT_LOSS_MEDICATION_BEFORE]?.data,
  );

  const collectedData = mdq.collectedData;

  const rules = {
    medicationTake: (attributes: any) => {
      if (isYesSelected) {
        return {
          presence: {allowEmpty: false, message: `^This field can't be blank`},
          length: {
            minimum: 5,
            tooShort: '^This field is too short (minimum is %{count} characters)',
            tokenizer: function (value: any) {
              return trimSpaces(value);
            },
          },
        };
      }
      return undefined;
    },
    howLong: (attributes: any) => {
      if (isYesSelected) {
        return {
          presence: {allowEmpty: false, message: `^This field can't be blank`},
          length: {
            minimum: 5,
            tooShort: '^This field is too short (minimum is %{count} characters)',
            tokenizer: function (value: any) {
              return trimSpaces(value);
            },
          },
        };
      }
      return undefined;
    },
  };

  const {inputs, setInputs, trimSpaces, validation} = useInput(
    {
      selectedOption: currentAnswer?.selectedOption || '',
      medicationTake: currentAnswer?.medicationTake || '',
      howLong: currentAnswer?.howLong || '',
    },
    {rules},
  );

  const isYesSelected = inputs.selectedOption === YES_NO_VALUES.YES;

  const onClickSubmit = () => {
    if (isYesSelected) {
      const errors = validation.getErrors();
      if (errors) {
        return;
      }
    }
    const answerData = {
      key: WEIGHT_LOSS_TWO_KEYS.TAKE_WEIGHT_LOSS_MEDICATION_BEFORE,
      index: questionnaire?.index || '',
      data: {
        selectedOption: inputs?.selectedOption,
        medicationTake: isYesSelected ? inputs?.medicationTake : '',
        howLong: isYesSelected ? inputs?.howLong : '',
      },
      order: answerOrder + 1,
    };

    dispatch(setQuestionnaireAnswer(answerData));
    dispatch(
      submitOrLogMdq({
        data: {
          uid: medicalUUID,
          methodKey: methodKey.toLowerCase(),
          status: 'draft',
          data: {
            ...collectedData,
            [WEIGHT_LOSS_TWO_KEYS.TAKE_WEIGHT_LOSS_MEDICATION_BEFORE]: answerData,
          },
        },
      }),
    );
    // dispatch(setNextQuestionnaire({value: WEIGHT_LOSS_KEYS.BMI_CALCULATOR}));
    if (isYesSelected) {
      history.push(PAGE_URLS.MDQ2_DID_MEDICATION_WORK + window.location.search);
      return;
    }

    history.push(PAGE_URLS.MDQ2_MEDICAL_HISTORY + window.location.search);
  };

  const renderMedicationTake = () => {
    if (!isYesSelected) return null;
    return (
      <UiBox mt={12}>
        <UiText fontWeight="700" mb={2.5}>
          {questionnaire?.questions?.sub}
        </UiText>
        <UiFormControl id="medicationTake" isRequired isInvalid={!!validation?.errors?.medicationTake}>
          <UiTextarea
            bg="white"
            value={inputs.medicationTake}
            onChange={({target}) => setInputs({medicationTake: target.value})}
            minH="100px"
            borderRadius={10}
          />
          {validation?.errors?.medicationTake && (
            <UiFormErrorMessage>{validation?.errors?.medicationTake[0]}</UiFormErrorMessage>
          )}
        </UiFormControl>
      </UiBox>
    );
  };

  const renderHowLong = () => {
    if (!isYesSelected) return null;
    return (
      <UiBox mt={8}>
        <UiText fontWeight="700" mb={2.5}>
          {questionnaire?.questions?.sub2}
        </UiText>
        <UiFormControl id="howLong" isRequired isInvalid={!!validation?.errors?.howLong}>
          <UiTextarea
            bg="white"
            value={inputs.howLong}
            onChange={({target}) => setInputs({howLong: target.value})}
            minH="100px"
            borderRadius={10}
          />
          {validation?.errors?.howLong && <UiFormErrorMessage>{validation?.errors?.howLong[0]}</UiFormErrorMessage>}
        </UiFormControl>
      </UiBox>
    );
  };

  const renderQuestions = () => {
    if (!isYesSelected) return null;
    return (
      <UiBox w="100%" className="animate__animated animate__fadeInLeft">
        {renderMedicationTake()}
        {renderHowLong()}
      </UiBox>
    );
  };

  return (
    <UiQuestionnaireLayout
      backIcon
      actions={questionnaire?.actions}
      handleOnNext={onClickSubmit}
      checkoutStep={questionnaire?.checkoutStep}
      pageTitle={questionnaire?.title}
      disableNext={!inputs.selectedOption}
    >
      <Col textAlign="center" mt={20} px={2}>
        <UiText fontSize="xl" fontWeight="bold" mb={6}>
          {questionnaire?.questions?.default}
        </UiText>
        <UiSimpleGrid columns={2} spacing={4}>
          {questionnaire?.options?.map(value => (
            <UiButton
              key={`${value.label}-${value.value}`}
              title={value.label}
              variant={inputs.selectedOption === value.value ? 'primary' : 'outline'}
              onClick={() => setInputs({selectedOption: value.value})}
              whiteSpace="pre-wrap"
              color="trueBlack"
              borderColor="trueBlack"
              fontWeight={600}
              borderRadius={48}
              _hover={{}}
            />
          ))}
        </UiSimpleGrid>
        {renderQuestions()}
      </Col>
    </UiQuestionnaireLayout>
  );
};

export default MDQ2Questionnaire7Screen;
